import Button from '@atoms/Button/Button';
import Dropdown from '@atoms/Dropdown/Dropdown';
import Text from '@atoms/Text/Text';
import TextInput from '@atoms/TextInput/TextInput';
import { IEditWishlist } from '@models/IEditWishlist';
import IFormValues from '@models/IFormValues';
import DrawerWrapper from '@molecules/DrawerWrapper/DrawerWrapper';
import Form from '@molecules/Form/Form';
import { useWishlistStore } from '@state/WishlistStore';
import React, { useState } from 'react';
interface IWishlistForm {
  inputLabel: string;
  submitLabel: string;
  currentName: string;
  onSubmit: (values: IFormValues) => void;
}

const WishlistForm = ({
  inputLabel,
  submitLabel,
  currentName,
  onSubmit,
}: IWishlistForm) => (
  <Form
    formSubmit={onSubmit}
    options={{
      defaultValues: {
        wishlistName: currentName,
      },
    }}
  >
    <TextInput label={inputLabel} id="wishlistName" />
    <div className="tw-pt-10">
      <Button
        variant="grey80"
        label={submitLabel}
        type="submit"
        buttonClasses="max-md:tw-w-full"
      />
    </div>
  </Form>
);

const EditWishlist = ({
  containerProps,
  copyModal,
  deleteModal,
  editDropdown,
  editLabel,
  renameModal,
  title,
  wishlistId,
}: IEditWishlist & { readonly title: string; readonly wishlistId: number }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    addWishlist,
    fetchWishlists,
    wishlists,
    updateWishlist,
    deleteWishlist,
  } = useWishlistStore();
  const [activeAction, setActiveAction] = useState<{
    title: string;
    description: string;
    content: React.JSX.Element;
  }>();

  const handleDelete = async () => {
    try {
      await deleteWishlist(wishlistId);
      await fetchWishlists();
    } catch (error) {
      console.error('Error deleting wishlist:', error);
    } finally {
      setDrawerOpen(false);
    }
  };

  const handleCopy = async (values: IFormValues, newList: boolean) => {
    if (!values.wishlistName) return;

    try {
      const existingListData = wishlists.find((item) => item.id === wishlistId);

      if (existingListData) {
        if (newList) {
          // Duplicate List
          await addWishlist({
            likes: existingListData.likes,
            name: values.wishlistName as string,
          });
        } else {
          // Rename List
          await updateWishlist({
            ...existingListData,
            name: values.wishlistName as string,
          });
        }
      }
    } catch (error) {
      console.error('Failed to copy wishlist:', error);
    } finally {
      await fetchWishlists();
      setDrawerOpen(false);
    }
  };

  const options = [
    {
      dropdownLabel: editDropdown.deleteLabel,
      title: deleteModal.title,
      description: '',
      content: (
        <div>
          <Text variant="body" className="tw-pb-10 md:tw-pb-12">
            {deleteModal.body}
          </Text>
          <Button
            variant="grey80"
            buttonClasses="max-md:tw-w-full"
            label={deleteModal.submitLabel}
            onClick={handleDelete}
          />
        </div>
      ),
    },
    {
      dropdownLabel: editDropdown.renameLabel,
      title: renameModal.title,
      description: '',
      content: (
        <WishlistForm
          {...renameModal}
          currentName={title}
          onSubmit={(values) => handleCopy(values, false)}
        />
      ),
    },
    {
      dropdownLabel: editDropdown.copyLabel,
      title: copyModal.title,
      description: copyModal.description ?? '',
      content: (
        <WishlistForm
          {...copyModal}
          currentName={`${title} ${copyModal.copySuffix}`}
          onSubmit={(values) => handleCopy(values, true)}
        />
      ),
    },
  ];

  return (
    <div {...containerProps}>
      <Dropdown
        triggerLabel={editLabel}
        options={options.map((option) => ({
          label: option.dropdownLabel,
          onClick: () => {
            setDrawerOpen(true);
            setActiveAction(option);
          },
        }))}
        buttonClasses="tw-w-full md:tw-w-auto"
      />
      <DrawerWrapper
        title={activeAction?.title ?? ''}
        description={activeAction?.description}
        setIsOpen={setDrawerOpen}
        isOpen={drawerOpen}
      >
        {activeAction?.content}
      </DrawerWrapper>
    </div>
  );
};

export default EditWishlist;
